import { create } from "zustand";
import { devtools } from "zustand/middleware";
import type { Cents } from "@/types/pricing.type";
import type { LoginModalOrigin } from "@/types/loginModal.type";
import { PAYWALL_TIMER_DURATION_SECONDS } from "@/hooks/usePaywallCountdown";

interface LoginModalState {
  isOpen: boolean;
  email: string | null;
  showCreationConfirmation: boolean;
  origin?: LoginModalOrigin;
  callback: (() => void) | null;
}

const DEFAULT_LOGIN_MODAL: LoginModalState = {
  isOpen: false,
  email: null,
  showCreationConfirmation: true,
  origin: undefined,
  callback: null,
};

interface AppStateStore {
  loginModal: LoginModalState;
  /**
   * Reset login modal to defaults, apply overrides, then open. The origin is
   * required: it is what `email pop up viewed` reports.
   */
  openLoginModal: (
    payload: Partial<Omit<LoginModalState, "isOpen" | "origin">> & {
      origin: LoginModalOrigin;
    },
  ) => void;
  /**
   * Close and reset the login modal to defaults, then run the opener's
   * callback. The modal is global and outlives the screen that opened it
   * (SHO-768): the callback runs on any close, a dismissal included, and
   * every close path goes through here so none can skip it (SHO-820).
   */
  closeLoginModal: () => void;
  /** True while a paywall is mounted, regardless of route. */
  paywallOpen: boolean;
  setPaywallOpen: (open: boolean) => void;
  /** True while the upsell flow is mounted. Mirrors paywallOpen. */
  upsellOpen: boolean;
  /**
   * True while a Behind the Scenes video plays over the series details page
   * (SHO-757). Mirrors paywallOpen: it hides the Zendesk launcher, which
   * would otherwise float over the video.
   */
  btsPlayerOpen: boolean;
  setBtsPlayerOpen: (open: boolean) => void;
  /**
   * Amount already paid on the subscription the upsell switches away from,
   * credited by the plan switch proration. null when nothing is credited: a
   * trialing sub is charged in full, and the dev entry knows no payment.
   */
  upsellCredit: Cents | null;
  /**
   * Show the triggering purchase was attributed to, reused as `show_title` on
   * the upsell's own `Purchase Complete`. Empty when there's no show context
   * (home paywall, dev entry).
   */
  upsellShowTitle: string;
  /**
   * Catalog id of the paywall plan the triggering purchase bought (`weekly`,
   * `exp_wmq_trial`, …), which picks the Yearly screen's copy (SHO-770,
   * `resolveYearlyCopy`). null when there's no purchase (dev entry).
   */
  upsellSourcePlanId: string | null;
  /** Open the upsell, carrying the credit its plan switch would apply. */
  openUpsell: (
    credit?: Cents | null,
    showTitle?: string,
    sourcePlanId?: string | null,
  ) => void;
  closeUpsell: () => void;
  /**
   * Epoch ms when the paywall offer ends, shared by the paywall and checkout
   * banners. In memory only (SHO-765): the countdown is scoped to the page
   * session, so a reload restarts it on the next paywall view.
   */
  paywallTimerEndsAt: number | null;
  /** Start the 10 min offer on the first paywall view; a no-op once running. */
  startPaywallTimer: () => void;
}

export const useAppStateStore = create<AppStateStore>()(
  devtools((set, get) => ({
    loginModal: DEFAULT_LOGIN_MODAL,
    openLoginModal: (payload) =>
      set({ loginModal: { ...DEFAULT_LOGIN_MODAL, ...payload, isOpen: true } }),
    closeLoginModal: () => {
      const { callback } = get().loginModal;
      // Close first: a callback that throws must not leave the modal stuck
      // open, every Escape re-running it (SHO-768).
      set({ loginModal: DEFAULT_LOGIN_MODAL });
      callback?.();
    },
    paywallOpen: false,
    setPaywallOpen: (open) => set({ paywallOpen: open }),
    upsellOpen: false,
    btsPlayerOpen: false,
    setBtsPlayerOpen: (open) => set({ btsPlayerOpen: open }),
    upsellCredit: null,
    upsellShowTitle: "",
    upsellSourcePlanId: null,
    openUpsell: (credit = null, showTitle = "", sourcePlanId = null) =>
      set({
        upsellOpen: true,
        upsellCredit: credit,
        upsellShowTitle: showTitle,
        upsellSourcePlanId: sourcePlanId,
      }),
    closeUpsell: () =>
      set({
        upsellOpen: false,
        upsellCredit: null,
        upsellShowTitle: "",
        upsellSourcePlanId: null,
      }),
    paywallTimerEndsAt: null,
    startPaywallTimer: () => {
      if (get().paywallTimerEndsAt != null) return;
      set({
        paywallTimerEndsAt: Date.now() + PAYWALL_TIMER_DURATION_SECONDS * 1000,
      });
    },
  })),
);

export const usePaywallOpen = () => useAppStateStore((s) => s.paywallOpen);
export const useUpsellOpen = () => useAppStateStore((s) => s.upsellOpen);
export const useBtsPlayerOpen = () => useAppStateStore((s) => s.btsPlayerOpen);
export const useUpsellCredit = () => useAppStateStore((s) => s.upsellCredit);
export const useUpsellShowTitle = () =>
  useAppStateStore((s) => s.upsellShowTitle);
export const useUpsellSourcePlanId = () =>
  useAppStateStore((s) => s.upsellSourcePlanId);
export const usePaywallTimerEndsAt = () =>
  useAppStateStore((s) => s.paywallTimerEndsAt);
